import React, { useState, useRef, useEffect } from "react";
import { useAppDispatch } from "../../../state/hooks";
import { showConfirmationDialog } from "../../../state/slices/confirmationDialogSlice";
import type { Branch } from "../../../types/user";

interface BranchSelectorProps {
  currentBranch: Branch | null;
  branches: Branch[];
  onBranchChange: (branch: Branch) => void;
}

export const BranchSelector: React.FC<BranchSelectorProps> = ({
  currentBranch,
  branches,
  onBranchChange,
}) => {
  const dispatch = useAppDispatch();
  const [isOpen, setIsOpen] = useState(false);
  const [isTriggerHovered, setIsTriggerHovered] = useState(false);
  const [hoveredOption, setHoveredOption] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleBranchSelect = (branch: Branch) => {
    // Validate branch object
    if (!branch || !branch.id || !branch.name) {
      console.error("[BranchSelector] Invalid branch object:", branch);
      setIsOpen(false);
      return;
    }

    // Don't show confirmation if selecting the same branch
    if (currentBranch?.id === branch.id) {
      setIsOpen(false);
      return;
    }

    // Show confirmation dialog with validated branch name
    const branchName = branch.name || "this branch";
    dispatch(
      showConfirmationDialog({
        title: "Switch Branch",
        message: `Are you sure you want to switch to "${branchName}"? The page will reload after switching.`,
        resolve: (confirmed: boolean) => {
          if (confirmed) {
            onBranchChange(branch);
            setIsOpen(false);
          }
        },
      })
    );
  };

  return (
    <div className="relative inline-block" ref={dropdownRef}>
      {/* Trigger Button */}
      <div
        className={`flex items-center gap-2 px-3 py-2 rounded-lg bg-gray-50 dark:bg-gray-800 border border-gray-300 dark:border-gray-600 cursor-pointer transition-all duration-200 text-sm font-medium text-gray-700 dark:text-gray-200 min-w-[120px] ${
          isTriggerHovered ? "bg-gray-100 dark:bg-gray-700 border-gray-400 dark:border-gray-500" : ""
        }`}
        onClick={() => setIsOpen(!isOpen)}
        onMouseEnter={() => setIsTriggerHovered(true)}
        onMouseLeave={() => setIsTriggerHovered(false)}
      >
        <span className="text-gray-700 dark:text-gray-200">
          {currentBranch?.name || "Select Branch"}
        </span>
        <span
          className={`text-xs text-gray-500 dark:text-gray-400 transition-transform duration-200 ml-auto ${
            isOpen ? "rotate-180" : "rotate-0"
          }`}
        >
          ▼
        </span>
      </div>

      {/* Dropdown Content */}
      <div
        className={`absolute top-full left-0 right-0 mt-1 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-300 dark:border-gray-600 z-[1000] overflow-hidden transition-all duration-150 max-h-[300px] overflow-y-auto ${
          isOpen
            ? "translate-y-0 scale-100 opacity-100 visible"
            : "-translate-y-1 scale-95 opacity-0 invisible"
        }`}
      >
        {branches.length === 0 ? (
          <div className="px-3 py-2.5 text-sm text-gray-500 dark:text-gray-400 text-center">
            {currentBranch ? (
              <span>Only {currentBranch.name} available</span>
            ) : (
              <span>No branches available</span>
            )}
          </div>
        ) : (
          branches
            .filter((branch) => branch && branch.id && branch.name) // Filter out invalid branches
            .map((branch) => {
              const isActive = currentBranch?.id === branch.id;
              const isHovered = hoveredOption === branch.id;

              return (
                <div
                  key={branch.id}
                  className={`px-3 py-2.5 text-sm text-gray-700 dark:text-gray-200 cursor-pointer transition-colors duration-150 border-b border-gray-100 dark:border-gray-700 last:border-0 ${
                    isActive
                      ? "bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 font-semibold"
                      : isHovered
                      ? "bg-gray-100 dark:bg-gray-700"
                      : ""
                  }`}
                  onClick={() => handleBranchSelect(branch)}
                  onMouseEnter={() => setHoveredOption(branch.id)}
                  onMouseLeave={() => setHoveredOption(null)}
                >
                  {branch.name || "Unknown Branch"}
                </div>
              );
            })
        )}
      </div>
    </div>
  );
};
